import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Calculator, Clock, FolderOpen, Search, CheckCircle, X } from 'lucide-react';
import MobileBottomSheet from './MobileBottomSheet';
import DocumentCard from '../ui/DocumentCard';
import { getTheme } from '../../utils/theme';
import { loadGooglePicker, downloadDriveFile } from '../../utils/googleDrivePicker';

import { useChat } from '../../contexts/ChatContext';
import ReactMarkdown from 'react-markdown';
import { uploadFileAndPrepareData, urlToBase64, fileToBase64 } from '../../utils/fileUpload';
import { extractFileContent } from '../../utils/textExtractor';
import { useAuth } from '../../contexts/AuthContext';

import { useDocuments } from '../../contexts/DocumentContext';
import { downloadFileViaProxy } from '../../services/gemini';

const TaxperView = ({ rightSidebarOpen, onCloseIntelligence, triggerUpload, onUploadTriggered, activeFilter = 'all', onFilterChange }) => {
  const { messages, sendMessage, isLoading, streamingMessage } = useChat();
  const { currentUser } = useAuth();
  const { documents, uploadDocument } = useDocuments();
  const fileInputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [localThinking, setLocalThinking] = useState(false);
  const isThinking = isLoading || localThinking;
  const [showAttachDropdown, setShowAttachDropdown] = useState(false);
  const [showCloudSubmenu, setShowCloudSubmenu] = useState(false);
  const [showMobileInsights, setShowMobileInsights] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const chatHistoryRef = useRef(null);
  const attachDropdownRefMobile = useRef(null);
  const attachButtonRefMobile = useRef(null);
  const attachDropdownRefDesktop = useRef(null);
  const attachButtonRefDesktop = useRef(null);

  // Detect mobile (768px breakpoint)
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isSidebarUpload = useRef(false);

  // Handle external upload trigger (from sidebar)
  useEffect(() => {
    if (triggerUpload && fileInputRef.current) {
      isSidebarUpload.current = true;
      fileInputRef.current.click();
      onUploadTriggered?.();
    }
  }, [triggerUpload, onUploadTriggered]);

  const handleDocumentClick = async (document) => {
    if (isMobile) {
      setShowMobileInsights(false);
    }

    // Analyze the clicked document
    if (document) {
      setLocalThinking(true);
      try {
        let base64Data = null;
        let extractedText = null;
        const isDocx = document.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          document.mimeType === 'text/plain';

        const isFirebaseStorage = document.url?.includes('firebasestorage.googleapis.com');

        if (document.url) {
          try {
            if (!isFirebaseStorage) {
              const response = await fetch(document.url);
              if (!response.ok) throw new Error("Fetch failed");
              const blob = await response.blob();

              if (isDocx) {
                extractedText = await extractFileContent(blob, document.name);
                if (extractedText && typeof extractedText === 'string') {
                  extractedText = extractedText.replace(/<[^>]*>?/gm, '\n');
                }
              } else {
                base64Data = await new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result.split(',')[1]);
                  reader.readAsDataURL(blob);
                });
              }
            } else {
              // Proxy Fallback for Firebase Storage
              console.log("Using proxy for sidebar document click (Storage URL)");
              const proxyResult = await downloadFileViaProxy(document.url);
              base64Data = proxyResult.base64Data;

              // Extract text if it's a document Gemini can't read as base64
              if (isDocx) {
                try {
                  const binaryString = window.atob(base64Data);
                  const bytes = new Uint8Array(binaryString.length);
                  for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                  }
                  const blob = new Blob([bytes], { type: document.mimeType });
                  extractedText = await extractFileContent(blob, document.name);
                  if (extractedText && typeof extractedText === 'string') {
                    extractedText = extractedText.replace(/<[^>]*>?/gm, '\n');
                  }
                } catch (e) {
                  console.error("Error extracting text from proxy data:", e);
                }
              }
            }
          } catch (e) {
            console.error("Failed to fetch clicked document content:", e);
            // Even if fetch fails, we can still send the URL to backend
          }
        }

        let messageContent = `Please analyze this document: ${document.name}`;
        const attachments = [];

        if (extractedText) {
          messageContent += `\n\n--- Document Content ---\n${extractedText}\n-----------------`;
        } else if (document.url || base64Data) {
          // Safety: Don't send DOCX binary to Gemini as it causes 400 error
          const mime = document.mimeType?.toLowerCase() || '';
          const isGeminiUnsupported = mime.includes('word') || mime.includes('officedocument') || mime.includes('docx');

          if (!isGeminiUnsupported) {
            attachments.push({
              mimeType: document.mimeType,
              url: base64Data ? null : document.url, // Only send URL if we don't have data
              data: base64Data
            });
          } else {
            console.log('[TaxperView] Skipping binary attachment for Gemini-unsupported document type:', document.mimeType);
            messageContent += `\n\n(Note: Binary analysis for ${document.name} skipped. Analyzing via text metadata if available.)`;
          }
        }

        await sendMessage(
          messageContent,
          "You are Taxper. Analyze the provided document for tax compliance, deductions, and financial insights.",
          attachments
        );
      } catch (err) {
        console.error("Error analyzing clicked document:", err);
      } finally {
        setLocalThinking(false);
      }
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    try {
      const isSidebar = isSidebarUpload.current;
      // Reset flag immediately
      isSidebarUpload.current = false;

      // Use current filter for type if it's specific, otherwise default to 'tax'
      const docType = (activeFilter !== 'all') ? activeFilter : 'tax';

      // 1. If triggered from SIDEBAR, we want to save to 'Documents' list (Firestore)
      if (isSidebar) {
        await uploadDocument(file, 'taxper', docType, { category: docType });
        // Just return, no chat attachment needed for sidebar upload action
        return;
      }

      // 2. If triggered from CHAT (+ button), we do NOT save to 'Documents' list
      // Process file LOCALLY (base64) without uploading to Storage
      // specific user request: "storage a bir şey yazmamlıyız"

      const base64Data = await fileToBase64(file);

      // Extract text content for DOCX/TXT to avoid Gemini's unsupported MIME type error
      let extractedText = null;
      if (
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'text/plain'
      ) {
        try {
          extractedText = await extractFileContent(file);
          // Strip HTML tags if it's from DOCX (mammoth returns HTML)
          if (extractedText && typeof extractedText === 'string') {
            // Simple tag strip for AI consumption
            extractedText = extractedText.replace(/<[^>]*>?/gm, '\n');
          }
        } catch (extractErr) {
          console.error('Error extracting text for chat:', extractErr);
        }
      }

      setPendingAttachments(prev => [...prev, {
        mimeType: file.type,
        url: null, // No URL since we didn't upload
        data: base64Data, // Include base64 data for Gemini (for PDF/Images)
        textContent: extractedText, // Include extracted text for DOCX
        name: file.name,
        id: Date.now()
      }]);

      // Focus input
      const input = document.getElementById('taxperInput');
      if (input) input.focus();
    } catch (err) {
      console.error('File upload failed:', err);
    } finally {
      // Critical: Reset file input so checking the same file again triggers onChange
      e.target.value = '';
      setShowAttachDropdown(false);
      isSidebarUpload.current = false; // Ensure reset
    }
  };

  const handleGoogleDrivePick = async () => {
    try {
      await loadGooglePicker(async (docs, accessToken) => {
        console.log("📁 Selected Drive files:", docs);

        const attachments = [];
        for (const doc of docs) {
          try {
            console.log(`Downloading file: ${doc.name}...`);
            const fileData = await downloadDriveFile(doc.id, accessToken);
            attachments.push({
              mimeType: fileData.mimeType || doc.mimeType,
              data: fileData.data,
              url: doc.url || null,
              name: doc.name, // Add name for display
              id: doc.id // Add id for keying
            });
          } catch (err) {
            console.error(`Error downloading file ${doc.name}:`, err);
          }
        }

        if (attachments.length > 0) {
          // Queue attachments instead of sending immediately
          const docType = (activeFilter !== 'all') ? activeFilter : 'tax';
          const typedAttachments = attachments.map(a => ({ ...a, type: docType }));
          setPendingAttachments(prev => [...prev, ...typedAttachments]);
        } else {
          docs.forEach(doc => {
            // If content couldn't be downloaded, queue as link-only
            const docType = (activeFilter !== 'all') ? activeFilter : 'tax';
            setPendingAttachments(prev => [...prev, {
              mimeType: doc.mimeType,
              url: doc.url,
              name: doc.name,
              id: doc.id,
              type: docType
            }]);
          });
        }
        // Focus input so user can type
        const input = document.getElementById('taxperInput');
        if (input) input.focus();
      });
    } catch (error) {
      console.error('Error loading Google Drive Picker:', error);
    }

    setShowAttachDropdown(false);
    setShowCloudSubmenu(false);
  };
  const handleSend = async () => {
    if (!inputValue.trim() && pendingAttachments.length === 0) return;
    let content = inputValue.trim();
    setInputValue('');
    const attachments = [...pendingAttachments];
    setPendingAttachments([]);

    try {
      // Process attachments:
      // 1. Identify valid attachments for Gemini (Images, PDFs)
      // 2. Identify DOCX/Text attachments to append as text content
      const geminiAttachments = [];
      let appendedContent = '';

      attachments.forEach(att => {
        if (att.textContent) {
          // It's a DOCX/Text file we extracted
          appendedContent += `\n\n--- Document: ${att.name} ---\n${att.textContent}\n-----------------\n`;
        } else if (
          att.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ) {
          // Backup check: It's DOCX but no text content? Skip it for Gemini to avoid 400 error.
          console.warn('Skipping DOCX attachment for inlineData (not supported by Gemini):', att.name);
        } else {
          // Support PDF, Images, etc.
          // IMPORTANT: If we have data, use it. If not, send URL for backend fetching.
          geminiAttachments.push({
            mimeType: att.mimeType || 'application/pdf', // Fallback
            url: att.data ? null : att.url, // Only send URL if we don't have base64 data
            data: att.data
          });
        }
      });

      if (appendedContent) {
        content += appendedContent;
      }

      // Send message with Tax System Prompt and valid attachments
      await sendMessage(
        content || (geminiAttachments.length > 0 ? `Please analyze: ${geminiAttachments.map(a => a.name || 'uploaded file').join(', ')}` : 'Analyze attached documents'),
        "You are Taxper, a professional AI tax and finance assistant. Your goal is to help users with tax calculations, deductions, and financial planning. Provide accurate, professional, and helpful advice based on the provided documents and context.",
        geminiAttachments
      );
    } catch (err) {
      console.error('Failed to send message:', err);
      // Restore attachments if send fails? Maybe keep it simple for now.
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Suggestions for Taxper
  const suggestions = [
    {
      id: 1,
      text: 'Calculate my tax liability',
      category: 'TAX CALCULATION',
      icon: Calculator,
    },
    {
      id: 2,
      text: 'Identify potential deductions',
      category: 'DEDUCTIONS',
      icon: Search,
    },
    {
      id: 3,
      text: 'Review financial statement',
      category: 'FINANCIAL REVIEW',
      icon: FileText,
    },
    {
      id: 4,
      text: 'Check tax compliance',
      category: 'COMPLIANCE',
      icon: CheckCircle,
    },
  ]; // Added missing suggestions

  // Filter documents based on active filter
  const filteredDocs = (activeFilter === 'all'
    ? documents?.filter(d => d.app === 'taxper')
    : documents?.filter(doc => (doc.type === activeFilter || doc.category === activeFilter) && doc.app === 'taxper')) || [];

  // Map to UI fields
  const displayDocs = filteredDocs.map(d => ({
    ...d,
    title: d.title || d.name,
    filename: d.filename || d.name,
    badge: d.type || d.category || d.badge || 'tax',
    skeleton: false
  }));

  // Filter by search query
  const searchFilteredDocs = searchQuery.trim()
    ? displayDocs.filter(doc =>
      doc.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : displayDocs;

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    setTimeout(() => {
      // Auto-send logic could go here
    }, 100);

    // Send message directly
    sendMessage(
      suggestion,
      `You are Taxper. The user selected a quick action: "${suggestion}". Provide a professional and helpful response regarding this tax or finance topic.`
    );
    setInputValue('');
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      // Handle external files (from computer)
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        console.log('📎 Files dropped in Taxper:', files);
        setIsThinking(true); // TaxperView uses isThinking state

        try {
          const docType = (activeFilter !== 'all') ? activeFilter : 'tax';

          // Use local processing (Chat Only) instead of saving to Firestore/Storage
          const uploadPromises = files.map(async (file) => {
            const base64Data = await fileToBase64(file);

            let extractedText = null;
            if (
              file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
              file.type === 'text/plain'
            ) {
              try {
                extractedText = await extractFileContent(file);
                if (extractedText && typeof extractedText === 'string') {
                  extractedText = extractedText.replace(/<[^>]*>?/gm, '\n');
                }
              } catch (e) { console.error(e); }
            }

            return {
              mimeType: file.type,
              url: null, // No URL
              data: base64Data,
              textContent: extractedText,
              name: file.name,
              id: Date.now() + Math.random()
            };
          });

          const uploadedFiles = await Promise.all(uploadPromises);
          setPendingAttachments(prev => [...prev, ...uploadedFiles]);

          setTimeout(() => {
            const input = document.getElementById(isMobile ? 'taxperInput' : 'taxperInputDesktop');
            if (input) input.focus();
          }, 100);
        } catch (err) {
          console.error('External file drop upload failed:', err);
        } finally {
          setIsThinking(false);
        }
        return;
      }

      // Handle internal application items
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;

      const doc = JSON.parse(data);
      console.log('📎 Document dropped (Queued):', doc);

      if (doc.url && doc.name) {
        // Fetch content for existing doc
        let base64Data = null;
        let extractedText = null;

        // Skip direct fetch for Firebase Storage to avoid CORS errors in browser
        const isFirebaseStorage = doc.url.includes('firebasestorage.googleapis.com');

        try {
          if (doc.url && !isFirebaseStorage) {
            const response = await fetch(doc.url);
            const blob = await response.blob();

            if (doc.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || doc.mimeType === 'text/plain') {
              extractedText = await extractFileContent(blob, doc.name);
              if (extractedText && typeof extractedText === 'string') {
                extractedText = extractedText.replace(/<[^>]*>?/gm, '\n');
              }
            } else {
              base64Data = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.readAsDataURL(blob);
              });
            }
          } else if (isFirebaseStorage) {
            throw new Error("CORS-prone URL, using proxy");
          } else {
            console.error("Dropped document missing URL");
            alert("Could not retrieve document URL. Please try downloading and uploading manually.");
          }
        } catch (err) {
          if (!isFirebaseStorage) {
            console.error("Direct fetch failed, trying proxy...", err);
          }

          // Proxy Fallback
          try {
            if (doc.url) {
              const proxyResult = await downloadFileViaProxy(doc.url);
              base64Data = proxyResult.base64Data;
              // If mimeType was not known or generic, possibly update it from proxy result
              if (!doc.mimeType || doc.mimeType === 'application/octet-stream') {
                doc.mimeType = proxyResult.mimeType;
              }

              // Extract text if it's a document Gemini can't read as base64
              if (doc.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || doc.mimeType === 'text/plain') {
                try {
                  const binaryString = window.atob(base64Data);
                  const bytes = new Uint8Array(binaryString.length);
                  for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                  }
                  const blob = new Blob([bytes], { type: doc.mimeType });
                  extractedText = await extractFileContent(blob, doc.name);
                  if (extractedText && typeof extractedText === 'string') {
                    extractedText = extractedText.replace(/<[^>]*>?/gm, '\n');
                  }
                } catch (e) {
                  console.error("Error extracting text from proxy data:", e);
                }
              }
              console.log("Proxy download successful");
            } else {
              throw new Error("No URL for proxy download");
            }
          } catch (proxyErr) {
            console.error("Proxy download failed:", proxyErr);
            // NEW STRATEGY: Don't block! Allow sending just the URL to the backend.
            console.log("Proceeding with URL-only attachment for backend fetching.");
          }
        }

        // if (!base64Data && !extractedText) {
        //   return; // Don't add if we couldn't get content
        // }
        // ALLOW adding even without data, so backend can try fetching.

        // Queue the attachment instead of sending immediately
        setPendingAttachments(prev => [
          ...prev,
          {
            mimeType: doc.mimeType || 'application/pdf', // Fallback
            url: doc.url,
            data: base64Data, // Add fetched data
            textContent: extractedText, // Add extracted text
            name: doc.name,
            id: doc.id
          }
        ]);

        // Focus input so user can type - check both desktop and mobile IDs
        setTimeout(() => {
          const input = document.getElementById('taxperInput') || document.getElementById('taxperInputDesktop');
          if (input) input.focus();
        }, 100);
      }
    } catch (err) {
      console.error('Error handling drop in Taxper:', err);
    }
  };

  return (
    <div
      id="view-taxper"
      className="view active"
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        // console.log('Draging over TaxperView'); // Excessive logging can be noisy, but good for check
      }}
      onDrop={handleDrop}
    >
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileUpload}
        accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
      />
      <div className="taxper-chat-pane">
        <div className="chat-header">
          <span>Taxper Assistant</span>
          {isMobile && (
            <button
              className="chat-header-docs-button taxper-theme"
              onClick={() => setShowMobileInsights(true)}
              aria-label="View documents"
            >
              <FileText size={18} />
            </button>
          )}
        </div>
        <div
          className="chat-history"
          id="taxperChatHistory"
          ref={chatHistoryRef}
        >
          {messages.length === 0 && !isThinking ? (
            <div className="empty-state">
              <div className="empty-icon">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8H52C54.2091 8 56 9.79086 56 12V52C54 54.2091 54.2091 56 52 56H12C9.79086 56 8 54.2091 8 52V12C8 9.79086 9.79086 8 12 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M20 24L28 32L44 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M20 40H44M20 48H40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              <h1 className="empty-title">Start with Taxper</h1>
              <p className="empty-subtitle">Tax calculations, deductions, compliance</p>

              {/* Try Asking Section */}
              <div className="w-full max-w-md mx-auto mt-8 px-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-4">
                  TRY ASKING:
                </p>
                <div className="flex flex-col gap-3">
                  {suggestions.map((suggestion) => {
                    const IconComponent = suggestion.icon;
                    return (
                      <button
                        key={suggestion.id}
                        onClick={() => handleSuggestionClick(suggestion.text)}
                        className="flex items-center gap-3 p-3 rounded-xl border transition-all active:scale-98 suggestion-card-base"
                        style={{
                          minHeight: '56px',
                          backdropFilter: 'blur(12px)',
                          WebkitBackdropFilter: 'blur(12px)',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                        }}
                      >
                        <div
                          className="flex items-center justify-center rounded-lg flex-shrink-0 suggestion-icon-bg-base"
                          style={{
                            width: '40px',
                            height: '40px',
                          }}
                        >
                          <IconComponent size={20} />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="text-sm font-medium text-white">
                            {suggestion.text}
                          </div>
                          <div className="text-xs uppercase tracking-wide mt-1" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                            {suggestion.category}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div key={message.id} className={`chat-message ${message.role}`}>
                  {message.role === 'assistant' ? (
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  ) : (
                    message.content
                  )}
                </div>
              ))}
              {streamingMessage && (
                <div className="chat-message assistant">
                  <ReactMarkdown>{streamingMessage}</ReactMarkdown>
                  <span className="animate-pulse">▊</span>
                </div>
              )}
              {isThinking && !streamingMessage && (
                <div className="chat-message assistant">
                  <span className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                </div>
              )}
            </>
          )}
        </div>


        {/* Desktop: Input bar inside chat-pane, Mobile: Outside for fixed positioning */}
        {
          !isMobile ? (
            <div className="input-bar-wrapper">
              {/* Pending Attachments Preview (Desktop) */}
              {pendingAttachments.length > 0 && (
                <div className="flex flex-wrap gap-2 px-4 py-2 bg-white/5 border-t border-white/10">
                  {pendingAttachments.map((att, idx) => (
                    <div key={`${att.id}-${idx}`} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-[11px] text-white/90 border border-white/10">
                      <FileText size={12} className="text-blue-400" />
                      <span className="max-w-[150px] truncate">{att.name}</span>
                      <button
                        onClick={() => setPendingAttachments(prev => prev.filter((_, i) => i !== idx))}
                        className="p-0.5 rounded-full hover:bg-white/20 transition-colors"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="input-bar">
                <div className="input-container">
                  <div className="attach-btn-wrapper">
                    <button
                      ref={attachButtonRefDesktop}
                      className="attach-btn"
                      id="taxperAttachBtnDesktop"
                      title="Attach file"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAttachDropdown(prev => !prev);
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 2V18M2 10H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                    <div ref={attachDropdownRefDesktop} className={`attach-dropdown ${showAttachDropdown ? 'show' : ''}`} id="taxperAttachDropdownDesktop">
                      <button className="attach-menu-item" data-action="upload-photo" onClick={() => fileInputRef.current?.click()}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="2" y="4" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
                          <circle cx="6" cy="7" r="1.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                          <path d="M2 12L5 9L8 12L14 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Upload Photo
                      </button>
                      <button className="attach-menu-item" data-action="upload-file" onClick={() => fileInputRef.current?.click()}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4 2H10L14 6V14C14 14.5523 13.5523 15 13 15H4C3.44772 15 3 14.5523 3 14V3C3 2.44772 3.44772 2 4 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M10 2V6H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Upload File
                      </button>
                      <button className="attach-menu-item attach-menu-item-expandable" data-action="cloud-accounts" id="taxperCloudBtnDesktop" onClick={() => setShowCloudSubmenu(!showCloudSubmenu)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4 8C2.89543 8 2 8.89543 2 10C2 11.1046 2.89543 12 4 12H12C13.1046 12 14 11.1046 14 10C14 8.89543 13.1046 8 12 8C12 5.79086 10.2091 4 8 4C6.34315 4 4.84315 4.84315 4 6.34315" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Connect Cloud Accounts
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: 'auto' }}>
                          <path d="M4 6L6 8L8 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <div className={`attach-cloud-submenu ${showCloudSubmenu ? 'show' : ''}`} id="taxperCloudSubmenuDesktop">
                        <button onClick={handleGoogleDrivePick} className="attach-menu-item" data-action="google-drive">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 2L14 12H2L8 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M8 2L2 12H8V2Z" fill="currentColor" opacity="0.3" />
                          </svg>
                          Google Drive
                        </button>

                      </div>
                    </div>
                  </div>
                  <input type="text" className="input-field" id="taxperInputDesktop" placeholder="Ask me anything..." value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyPress={handleKeyPress} />
                  <button className="send-btn" id="taxperSendDesktop" title="Send message" onClick={handleSend} disabled={!inputValue.trim() && pendingAttachments.length === 0 || isThinking}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 7L12 2L7 12L6 7L2 7Z" fill="currentColor" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ) : null
        }
      </div >

      {/* Mobile: Input bar outside chat-pane for fixed positioning */}
      {
        isMobile && (
          <div className="input-bar-wrapper">
            {/* Pending Attachments Preview */}
            {pendingAttachments.length > 0 && (
              <div className="flex flex-wrap gap-2 px-4 py-2 bg-white/5 border-t border-white/10">
                {pendingAttachments.map((att, idx) => (
                  <div key={`${att.id}-${idx}`} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-[11px] text-white/90 border border-white/10">
                    <FileText size={12} className="text-blue-400" />
                    <span className="max-w-[150px] truncate">{att.name}</span>
                    <button
                      onClick={() => setPendingAttachments(prev => prev.filter((_, i) => i !== idx))}
                      className="p-0.5 rounded-full hover:bg-white/20 transition-colors"
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="input-bar">
              <div className="input-container">
                <div className="attach-btn-wrapper">
                  <button
                    ref={attachButtonRefMobile}
                    className="attach-btn"
                    id="taxperAttachBtn"
                    title="Attach file"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowAttachDropdown(prev => !prev);
                    }}
                    style={{
                      WebkitTapHighlightColor: 'transparent',
                      touchAction: 'manipulation',
                      pointerEvents: 'auto'
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 2V18M2 10H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>
                  {/* Mobile dropdown rendered via Portal to escape all stacking contexts */}
                  {isMobile && showAttachDropdown && createPortal(
                    <div
                      ref={attachDropdownRefMobile}
                      className="attach-dropdown show"
                      id="taxperAttachDropdown"
                      data-theme={getTheme()}
                      style={{
                        position: 'fixed',
                        zIndex: 99999,
                        // Remove hardcoded dark theme styles - let CSS handle theming
                        isolation: 'isolate',
                        opacity: 0,
                        visibility: 'hidden',
                        pointerEvents: 'none',
                        transition: 'opacity 0.15s ease-out',
                      }}
                    >
                      <button className="attach-menu-item" data-action="upload-photo" onClick={() => fileInputRef.current?.click()}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="2" y="4" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
                          <circle cx="6" cy="7" r="1.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                          <path d="M2 12L5 9L8 12L14 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Upload Photo
                      </button>
                      <button className="attach-menu-item" data-action="upload-file" onClick={() => fileInputRef.current?.click()}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4 2H10L14 6V14C14 14.5523 13.5523 15 13 15H4C3.44772 15 3 14.5523 3 14V3C3 2.44772 3.44772 2 4 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M10 2V6H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Upload File
                      </button>
                      <button className="attach-menu-item attach-menu-item-expandable" data-action="cloud-accounts" id="taxperCloudBtn" onClick={() => setShowCloudSubmenu(!showCloudSubmenu)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4 8C2.89543 8 2 8.89543 2 10C2 11.1046 2.89543 12 4 12H12C13.1046 12 14 11.1046 14 10C14 8.89543 13.1046 8 12 8C12 5.79086 10.2091 4 8 4C6.34315 4 4.84315 4.84315 4 6.34315" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Connect Cloud Accounts
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: 'auto' }}>
                          <path d="M4 6L6 8L8 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <div className={`attach-cloud-submenu ${showCloudSubmenu ? 'show' : ''}`} id="taxperCloudSubmenu">
                        <button onClick={handleGoogleDrivePick} className="attach-menu-item" data-action="google-drive">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 2L14 12H2L8 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M8 2L2 12H8V2Z" fill="currentColor" opacity="0.3" />
                          </svg>
                          Google Drive
                        </button>


                      </div>
                    </div>,
                    document.body
                  )}
                </div>
                <input type="text" className="input-field" id="taxperInput" placeholder="Ask me anything..." value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyPress={handleKeyPress} />
                <button className="send-btn" id="taxperSend" title="Send message" onClick={handleSend} disabled={!inputValue.trim() || isThinking}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 7L12 2L7 12L6 7L2 7Z" fill="currentColor" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Mobile Blur Effect - Behind Bottom Nav */}
      {
        isMobile && (
          <div
            className="fixed left-0 right-0 z-40 pointer-events-none"
            aria-hidden="true"
            style={{
              bottom: '0',
              height: 'calc(64px + env(safe-area-inset-bottom, 0px))',
              background: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
            }}
          />
        )
      }

      {/* Mobile Intelligence Bottom Sheet - Blue Themed */}
      {
        isMobile && (
          <MobileBottomSheet
            isOpen={showMobileInsights}
            onClose={() => {
              setShowMobileInsights(false);
              onCloseIntelligence?.();
            }}
            title="Tax Documents & Insights"
          >
            <div className="flex flex-col gap-4">
              {/* Search Input - MOBILE: 48px height, 16px font */}
              <input
                type="search"
                className="w-full h-12 md:h-[42px] px-3 rounded-2xl border outline-none text-base md:text-sm transition-all"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  color: '#000000',
                  WebkitTextFillColor: '#000000',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid rgba(0, 0, 0, 0.2)',
                  opacity: 1,
                }}
              />

              {/* Filter Chips */}
              <div className="flex gap-1.5 flex-wrap">
                {['All', 'Tax', 'Receipt', 'Statement'].map((filter) => {
                  const filterKey = filter.toLowerCase();
                  const isActive = activeFilter === filterKey;
                  return (
                    <button
                      key={filter}
                      onClick={() => onFilterChange(filterKey)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all active:scale-95 filter-chip-base ${isActive ? 'active' : ''}`}
                    >
                      {filter}
                    </button>
                  );
                })}
              </div>

              {/* Document List */}
              <div className="flex flex-col gap-1.5">
                {searchFilteredDocs.map((doc) => (
                  <DocumentCard
                    key={doc.id}
                    document={doc}
                    onClick={handleDocumentClick}
                    theme={getTheme()}
                    showSkeleton={doc.loading}
                  />
                ))}
              </div>
            </div>
          </MobileBottomSheet>
        )
      }
    </div >
  );
};

export default TaxperView;
